import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Boxes, Building, CalendarRange, DatabaseZap, SquareKanban, User } from "lucide-react";
import FilterTabContent from "./filters/FilterTabContent";
import OutlineTabContent from "./outline/OutlineTabContent";
import {
  MetricSection,
  GroupedBySection,
  SegmentBySection,
  AppearanceSection,
} from "./settings/SettingTabContent";
import { SelectField } from "../../../components/ui";

interface ReportSidebarProps {
  projectId: string;
  dashboardId: string;
  reportType: string;
  reportChartType: string;
  reportObjectType: string;
  reportViewMode: string;
  chartType: string;
  tableColumns: { key: string; title: string }[];
  visibleTableColumns: { key: string; title: string }[];
}

const Divider = () => <div className="border-t border-gray-200" />;

const ReportSidebar = ({
  projectId,
  dashboardId,
  reportType,
  reportChartType,
  reportObjectType,
  reportViewMode,
  chartType,
  tableColumns,
  visibleTableColumns,
}: ReportSidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const viewModeEnabled = reportViewMode === "true";

  const optionDataTypes = [
    { value: "SALE", label: t("Deal"), icon: <SquareKanban className="w-4 h-4" /> },
    { value: "COMPANY", label: t("Account"), icon: <Building className="w-4 h-4" /> },
    { value: "CONTACT", label: t("Contact"), icon: <User className="w-4 h-4" /> },
    { value: "PRODUCT", label: t("Product"), icon: <Boxes className="w-4 h-4" /> },
    { value: "ACTIVITY", label: t("Activity"), icon: <CalendarRange className="w-4 h-4" /> },
  ];

  return (
    <div className="w-[401px] bg-white border border-gray-200 rounded-xl shadow-sm overflow-y-auto max-h-[calc(100vh-64px)]">
      <div className="p-5 space-y-4">
        {/* ─── Data Source ──────────────────────────────────── */}
        <div className="flex items-center">
          <div className="flex-none h-8 px-2 py-1.5 flex items-center space-x-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-l-md border border-gray-300">
            <DatabaseZap className="h-4 w-4 text-gray-500" />
            <div className="pr-1">{t("Data Type")}</div>
          </div>
          <div className="flex-1">
            <SelectField
              value={optionDataTypes.find((item) => item.value === reportObjectType)}
              onChange={(value) => {
                if (value?.value === "SALE") {
                  navigate(
                    `/workspaces/${projectId}/reports/dashboard/${dashboardId}/new?key=Reports&reportType=${
                      reportType || ""
                    }&reportChartType=${
                      reportChartType || ""
                    }&reportObjectType=SALE&reportViewMode=${viewModeEnabled}`
                  );
                } else {
                  navigate(
                    `/workspaces/${projectId}/reports/dashboard/${dashboardId}/new?key=Reports&reportType=${
                      reportType === "goal" ? "chart" : reportType
                    }&reportChartType=${
                      reportType === "goal" ? "line" : reportChartType
                    }&reportObjectType=${value?.value || "SALE"}&reportViewMode=${viewModeEnabled}`
                  );
                }
              }}
              options={optionDataTypes}
              formatOptionLabel={(option) => {
                return (
                  <div
                    className={"w-full hover:text-blue-600 flex items-center space-x-2"}
                    key={option.value}
                  >
                    <div>{option.icon}</div>
                    <div>{option.label}</div>
                  </div>
                );
              }}
              placeholder={t("Select...")}
              stylesOverride={{
                control: (base) => ({
                  ...base,
                  minHeight: 32,
                  height: 32,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: 8,
                  borderBottomRightRadius: 8,
                  borderLeft: "none",
                  borderColor: "#d1d5dc",
                  boxShadow: "none",
                  ":hover": {
                    borderColor: "#d1d5db",
                  },
                }),
              }}
            />
          </div>
        </div>

        {/* ─── Filters ─────────────────────────────────────── */}
        <FilterTabContent objectType={reportObjectType} chartType={chartType} />

        <Divider />

        {/* ─── Metric (group box) ──────────────────────────── */}
        <MetricSection chartType={chartType} objectType={reportObjectType} />

        {/* ─── Grouped by (group box) ─────────────────────── */}
        <GroupedBySection
          chartType={chartType}
          objectType={reportObjectType}
          projectId={projectId}
        />

        {/* ─── Segment by ─────────────────────────────────── */}
        <SegmentBySection
          chartType={chartType}
          objectType={reportObjectType}
          projectId={projectId}
        />

        <Divider />

        {/* ─── Appearance (Title, Subtitle, Colors) ────────── */}
        <AppearanceSection chartType={chartType} />

        {/* ─── Table Columns ──────────────────────────────── */}
        {tableColumns && tableColumns.length > 0 && (
          <>
            <Divider />
            <div className="space-y-3">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t("Table Columns")}
              </div>
              <OutlineTabContent
                tableColumns={tableColumns}
                visibleTableColumns={visibleTableColumns}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportSidebar;
